import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Page from '@glowforge/ui-common/components/contentful-views/page';
import '@glowforge/ui-common/initializers/contentful-page';
import ReferralInfoContext from '../../context/ReferralInfoContext';
import useFeatureFlags from '../../hooks/use-feature-flags';
import { pagePropType } from '../../prop-types/contentful';
import { productPropType } from '../../prop-types/storefront';
import { reviewsPropType } from '../../prop-types/yotpo';
import FeaturesDisplayWidget from '../features-display/features-display-widget';
import CustomPageMetadata from '../custom-page-metadata';
import GlowforgeProduct from '../glowforge-product/glowforge-product';
// eslint-disable-next-line max-len
import ComparisonTableWithImages from '../comparison-table-with-images/comparison-table-with-images';
import GlowforgeProductAccordion from '../glowforge-product-accordion/glowforge-product-accordion';
import ComparisonTable from '../comparison-table/comparison-table';

import './glowforge-products-page.less';

const ComparisonTableWrapper = (props) => (
  <ComparisonTable
    comparisonData={_.get(props, 'sections[0].fields')}
    callToActionText={_.get(props, 'textStrings[0].fields.text')}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props} />
);

const GlowforgeProductsPage = ({
  page,
  products,
  productId,
  proReviews,
  plusReviews,
  basicReviews,
}) => {
  const featureFlags = useFeatureFlags();
  const { referralInfo, ensureReferralInfo } = useContext(ReferralInfoContext);
  const productName = _.find(_.get(page, 'fields.unorderedShortText'), {
    fields: { id: `${productId}-product-name` },
  });
  const printerType = _.get(productName, 'fields.text');
  const stringSubstitutions = [
    { name: 'printer-type', value: printerType },
  ];
  const showTitle = !_.isEmpty(productId);

  // Load referral data
  useEffect(() => {
    ensureReferralInfo();
  }, []);

  const showReferral = (
    !_.isEmpty(referralInfo)
      && featureFlags['referral-strike-through-on-products-page']
  );

  const customSections = {
    'glowforge-pro-section': {
      component: GlowforgeProduct,
      props: {
        imageSectionId: 'glowforge-pro-product-description',
        buttonId: 'buy-now-pro',
        product: products.pro,
        showReferralInfo: showReferral,
        showTitle,
        showAffirm: true,
        reviews: proReviews,
        jsonLdTemplateId: 'glowforge-pro-json-ld-template',
      },
    },
    'glowforge-plus-section': {
      component: GlowforgeProduct,
      props: {
        imageSectionId: 'glowforge-plus-product-description',
        buttonId: 'buy-now-plus',
        product: products.plus,
        showReferralInfo: showReferral,
        showTitle,
        showAffirm: true,
        reviews: plusReviews,
        jsonLdTemplateId: 'glowforge-plus-json-ld-template',
      },
    },
    'glowforge-basic-section': {
      component: GlowforgeProduct,
      props: {
        imageSectionId: 'glowforge-basic-product-description',
        buttonId: 'buy-now-basic',
        product: products.basic,
        showReferralInfo: showReferral,
        showTitle,
        showAffirm: true,
        reviews: basicReviews,
        jsonLdTemplateId: 'glowforge-basic-json-ld-template',
      },
    },
    'glowforge-whats-included-accordion': {
      component: GlowforgeProductAccordion,
    },
    'comparison-table-with-images': {
      component: ComparisonTableWithImages,
      props: {
        products,
      },
    },
    'gf-product-page-comparison-chart': {
      component: ComparisonTableWrapper,
      props: {
        products,
        showCallToAction: false,
        stringSubstitutions,
      },
    },
    'gf-products-air-filter-section': {
      component: GlowforgeProduct,
      props: {
        imageSectionId: 'gf-af-description',
        product: products.air,
        showDeliveryDate: true,
      },
    },
    'features-display': { component: FeaturesDisplayWidget },
  };
  return (
    <>
      <CustomPageMetadata page={page} />
      <Page
        className='GlowforgeProductsPage'
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...page.fields}
        title={null}
        customSections={customSections}
        stringSubstitutions={stringSubstitutions} />
    </>
  );
};

GlowforgeProductsPage.propTypes = {
  page: pagePropType.isRequired,
  products: PropTypes.objectOf(productPropType).isRequired,
  productId: PropTypes.string,
  proReviews: reviewsPropType,
  plusReviews: reviewsPropType,
  basicReviews: reviewsPropType,
};

GlowforgeProductsPage.defaultProps = {
  productId: null,
  proReviews: null,
  plusReviews: null,
  basicReviews: null,
};

export default GlowforgeProductsPage;
